import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { MenuIcon } from '@/components/MenuIcon';
import { apiFetch } from '@/lib/api/client';
import { useSession } from '@/lib/auth/session';
import { isAdministrator, type SiteTaskItem } from '@/lib/types';
import { mockList } from '@/lib/mock/db';
import { queryKeys } from '@/lib/queryKeys';
import { useSites, useWeeks } from '@/lib/queries';
import { findCurrentWeek } from '@/lib/utils/week';
import { useInboxCount } from '@/pages/approval/useApproval';
import { useMyProfile } from '@/pages/home/useMyProfile';
import { useDismissedTodoKeys, useTodoMutations, useTodos, type ServerTodo } from '@/pages/home/useTodos';
import { daysUntil } from '@/pages/home/widgets/format';
import { WidgetFrame } from '@/pages/home/widgets/WidgetFrame';

/**
 * TO DO 할일. 숨길 수 없는 고정 위젯이다.
 *
 * 세 겹으로 되어 있다.
 *
 *  1. **직접 적은 할일**(서버 `MANUAL`) — 여기서 추가·체크·삭제까지 끝난다. 전용 화면을 만들지 않는 것은
 *     의도적이다. 할일을 적으러 다른 화면에 들어가야 한다면 아무도 쓰지 않는다.
 *  2. **배정받은 일**(서버 `ASSIGNED`) — 누군가 "언제까지 해 주세요" 로 보낸 것(공지 기한 배포 포함).
 *     내용은 못 고치지만 체크와 삭제는 된다. 서버는 지우지 않고 <b>가린다</b>(V230) — 내 목록에서만
 *     빠지고 요청자 진행률에는 「안 함」 으로 남으므로, 치우는 것이 배정에서 빠져나가는 길이 아니다.
 *
 * 체크한 일은 **위젯에서 빠진다**(사용자 지시 2026-10-02). 한때 줄을 그어 아래에 남겨 두었는데
 * (2026-09-03 — 체크가 곧 사라짐이면 삭제와 구분되지 않는다는 이유), 지금은 전체보기(`/todos`)가
 * 끝낸 일까지 전부 쌓아 두므로 잘못 누른 줄은 거기서 되돌린다. 위젯은 「지금 할 것」 만 세운다.
 *  3. **시스템이 찾아낸 할일** — 자격증 만료·비밀번호(서버 `DERIVED`), 그리고 이번 주 주간보고 미작성·
 *     미처리 요청·결재 대기(여기서 계산). 저장하지 않고 매번 계산하므로 체크박스가 없다 — 체크할 행이
 *     없고, 원본을 처리해야 사라진다.
 *
 *     **치우는 것은 전부 된다**(사용자 지시 2026-09-03). 목록을 정리하는 것은 보는 사람의 몫이라,
 *     "이건 지금 안 한다" 를 못 고르게 하면 목록 전체가 「어차피 안 없어지는 것들」 로 읽힌다.
 *     치운 기록은 <b>서버에 남는다</b>(V231 · 계정 단위) — 웹에서 치운 줄이 모바일에 그대로 서 있으면
 *     지운 사람은 안 지워졌다고 읽는다. 키에 원본 상태(만료일·건수·주차)가 박혀 있어, 자격증을
 *     갱신하거나 건수가 달라지면 <b>저절로 되살아난다.</b>
 *
 * 파생 할일이 쓰는 자료(주차·현장·요청)는 각각 따로 도착한다. 그것을 기다리느라 위젯이 비어 있지
 * 않도록, 이 위젯의 로딩 상태는 <b>서버 목록 하나만</b> 본다. 나머지는 도착하는 대로 끼어든다.
 */
export function TodoWidget() {
  const todos = useTodos();
  const { add, setDone, remove } = useTodoMutations();
  const derived = useDerivedTodos();
  // 서버 파생은 목록에서 이미 빠져 오고, 이 목록은 <b>여기서 계산한</b> 파생을 거르는 데만 쓴다.
  const dismissed = useDismissedTodoKeys().data ?? [];
  const [draft, setDraft] = useState('');
  // 기한은 <b>접어 둔다</b>. 대부분의 할일은 "오늘 안에" 라 날짜가 필요 없고, 좁은 위젯에서
  // 입력칸을 둘로 나누면 정작 제목 칸이 좁아진다. 필요한 사람만 달력을 펴서 D-day 를 붙인다.
  const [dueOpen, setDueOpen] = useState(false);
  const [due, setDue] = useState('');

  const submit = () => {
    const content = draft.trim();
    if (!content || add.isPending) return;
    add.mutate(
      { content, dueDate: due || null },
      {
        onSuccess: () => {
          setDraft('');
          setDue('');
          setDueOpen(false);
        },
      },
    );
  };

  // 서버 목록에는 저장된 할일과 파생(자격증 만료)이 섞여 온다. 파생은 체크박스 없이 링크로 그린다 —
  // 저장된 행이 아니라서 체크를 눌러도 갱신할 대상이 없다.
  const all = todos.data ?? [];
  // 끝낸 줄은 위젯에서 빠진다(머리말). 체크하는 순간 목록을 다시 받아 그 줄이 사라지고,
  // 되돌리거나 되짚는 일은 전체보기가 받는다. 서버 정렬은 완료 여부를 보지 않는다
  // (TodoItemMapper.findByUser — 체크해도 나머지 줄의 순서가 그대로다).
  const rows = all.filter((item) => item.kind !== 'DERIVED' && !item.done);
  const serverDerived: DerivedTodo[] = all
    .filter((item) => item.kind === 'DERIVED')
    .map((item) => ({
      id: item.id,
      content: item.dueDate ? `${item.content} (${item.dueDate})` : item.content,
      href: item.href ?? '/mypage',
      // 투표만 평상 색이다. 나머지 파생 항목은 <b>지나면 손해가 나는 것</b>(자격증 만료·키 만료·
      // 휴무 미신청)이라 붉게 세우지만, 투표는 안 해도 잃는 것이 없다 — 같은 색으로 세우면
      // 정작 붉은 줄이 배경이 된다.
      tone: item.id.startsWith('poll') ? 'normal' : 'danger',
      // 서버가 정한다. 옛 응답(필드가 없는)은 치울 수 있는 것으로 본다 — 없던 규칙을 새로
      // 들이대면 배포 직후의 캐시된 목록이 통째로 못 지우는 줄이 된다.
      dismissible: item.dismissible !== false,
    }));
  // 서버 파생은 서버가 이미 걸렀다. 여기서 거르는 것은 화면이 계산한 줄뿐이다.
  const notices = [...serverDerived, ...derived.filter((item) => !dismissed.includes(item.id))];

  const openCount = rows.length + notices.length;
  // 최신이 맨 위, 위젯은 15줄까지(사용자 지시 2026-10-20 · DOC-123). 알림(파생)은 처리해야 사라지는
  // 줄이라 앞에 두고, 저장된 할일만 적은 시각 역순으로 다시 세운다 — 서버 정렬(마감일 순)은 전체보기와
  // 앱이 함께 쓰므로 여기서만 바꾼다. 시각이 없는 줄(옛 응답)은 안정 정렬로 서버 순서 그대로 뒤에 선다.
  const newestFirst = [...rows].sort((a, b) => (b.createdAt ?? '').localeCompare(a.createdAt ?? ''));
  const shownNotices = notices.slice(0, WIDGET_LIMIT);
  const shownRows = newestFirst.slice(0, WIDGET_LIMIT - shownNotices.length);
  const hiddenCount = openCount - shownNotices.length - shownRows.length;

  return (
    <WidgetFrame
      title="TO DO 할일"
      icon="check"
      pending={todos.isPending}
      error={todos.error}
      // 목록이 비어도 empty 로 두지 않는다 — 입력칸이 남아 있어야 바로 적을 수 있다.
      // 이 문구는 서버가 "그런 목록 없음"(404)으로 답할 때만 쓰인다.
      emptyText="현재 등록된 할일이 없습니다."
      // 숫자는 <b>제목 옆</b>이다(사용자 지시 2026-09-30). 오른쪽 끝은 전체보기가 받는다 —
      // 둘을 같은 묶음에 두면 「⑦ 전체보기 ›」 로 붙어 서서 숫자가 링크의 일부로 읽힌다.
      titleBadge={
        openCount > 0 ? <span className="badge badge-blue">{openCount}</span> : <span className="badge badge-gray">완료</span>
      }
      // 15줄 밖에 남은 건수는 <b>제목 줄 오른쪽 끝</b>의 빨간 원이다(사용자 지시 2026-10-20). 파란 배지(전체 건수)와
      // 세는 것이 달라 자리를 나눴다. 상한은 999+.
      headerRight={hiddenCount > 0 ? <MoreBadge n={hiddenCount} /> : undefined}
      // 전체보기는 <b>내 작업 일정</b>(DOC-100). 위젯은 「지금 할 것」 만 세우고, 기간·진행률을
      // 적거나 「지난주에 뭘 했더라」 를 되짚는 일은 그 화면이 받는다.
      to="/todos"
    >
      <div className="space-y-0.5">
        {shownNotices.map((item) => (
          // 지우기 버튼은 링크 <b>밖</b>에 둔다. 안에 넣으면 누르는 순간 화면이 옮겨 가고,
          // 돌아와 보면 알림이 사라져 있어 무엇이 일어난 것인지 알 수 없다.
          <div key={item.id} className="group flex items-center gap-2 py-1.5 px-1 rounded-md hover:bg-slate-50">
            <Link to={item.href} className="flex min-w-0 flex-1 items-center gap-2">
              <span className="flex-shrink-0" style={{ color: item.tone === 'danger' ? 'var(--danger)' : 'var(--accent)' }}>
                <MenuIcon name="bell" size={13} />
              </span>
              {/* 잘린 글은 마우스를 올려 읽는다 — 좁은 노트북 화면에서는 대부분의 알림이 끝까지 안 보인다. */}
              <span className="min-w-0 flex-1 truncate text-ui-md" title={item.content} style={{ color: 'var(--text-secondary)' }}>
                {item.content}
              </span>
            </Link>
            {/* 치우기 버튼은 마우스를 올릴 때만 나타나게 하지 않는다 — 저장된 줄과 같은 규칙이다.
                보이지 않으면 「이건 못 지우는 줄」 로 읽힌다.

                그리고 <b>정말로 못 지우는 줄</b>에는 아예 세우지 않는다(교육 증빙 · V264). 눌러도
                서버가 거절하는 버튼을 두면, 그 거절 메시지를 읽기 전까지는 화면이 고장 난 것으로
                보인다. 대신 자물쇠를 둔다 — 「없는 것」 과 「못 하는 것」 은 다르다. */}
            {item.dismissible ? (
              <button
                onClick={() => remove.mutate(item.id)}
                className="btn-ghost p-0.5 flex-shrink-0"
                aria-label={`${item.content} 알림 지우기`}
                title="이 알림 지우기"
                style={{ color: 'var(--text-muted)' }}
              >
                <MenuIcon name="x" size={12} />
              </button>
            ) : (
              <span
                className="p-0.5 flex-shrink-0"
                title="처리해야 사라지는 항목입니다"
                style={{ color: 'var(--text-muted)' }}
              >
                <MenuIcon name="lock" size={12} />
              </span>
            )}
          </div>
        ))}

        {shownRows.map((item) => (
          <TodoRow
            key={item.id}
            item={item}
            onToggle={() => setDone.mutate({ id: item.id, done: !item.done })}
            onRemove={() => remove.mutate(item.id)}
          />
        ))}

        {notices.length === 0 && rows.length === 0 && (
          <p className="py-3 text-center text-ui-sm" style={{ color: 'var(--text-muted)' }}>
            현재 등록된 할일이 없습니다. 아래에 바로 추가할 수 있습니다.
          </p>
        )}
      </div>

      <div className="mt-3 space-y-1.5">
        <div className="flex gap-1.5">
          <input
            className="input-field input-sm"
            placeholder="할일 추가"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => event.key === 'Enter' && submit()}
          />
          <button
            className="btn-secondary flex-shrink-0"
            onClick={() => setDueOpen((open) => !open)}
            aria-label="기한 설정"
            aria-expanded={dueOpen}
            title="기한 설정"
            style={{ color: due ? 'var(--accent)' : undefined }}
          >
            <MenuIcon name="calendar" size={14} />
          </button>
          <button className="btn-secondary flex-shrink-0" onClick={submit} disabled={!draft.trim() || add.isPending} aria-label="할일 추가">
            <MenuIcon name="plus" size={14} />
          </button>
        </div>

        {(dueOpen || due) && (
          <div className="flex items-center gap-1.5">
            <input
              type="date"
              className="input-field input-xs"
              value={due}
              onChange={(event) => setDue(event.target.value)}
              onKeyDown={(event) => event.key === 'Enter' && submit()}
              aria-label="기한"
            />
            {/* 고른 날짜가 며칠 남은 것인지 저장 전에 보여 준다 — 목록에 붙을 배지와 같은 계산이다. */}
            {due && <DueBadge dueDate={due} />}
            {due && (
              <button className="btn-ghost flex-shrink-0" onClick={() => setDue('')} aria-label="기한 지우기">
                <MenuIcon name="x" size={12} />
              </button>
            )}
          </div>
        )}
      </div>
    </WidgetFrame>
  );
}

/** 위젯이 세우는 줄 수(알림 + 할일). 나머지는 전체보기가 받는다. */
const WIDGET_LIMIT = 15;

/** 15줄 밖에 남은 건수. 사이드바의 결재 대기 배지(Navigation `CountBadge`)와 같은 빨간 원이고 상한만 999+ 다. */
function MoreBadge({ n }: { n: number }) {
  return (
    <span
      title={`${n}건 더 있음`}
      style={{
        fontSize: '10px',
        fontWeight: 700,
        color: '#fff',
        background: '#dc2626',
        borderRadius: '9px',
        padding: '1px 6px',
        lineHeight: 1.5,
      }}
    >
      {n > 999 ? '999+' : n}
    </span>
  );
}

/**
 * 저장된 할일 한 줄. 위젯은 최신을 맨 위로 다시 세운다(DOC-123) — 서버 순서는 전체보기가 쓴다.
 *
 * 배정도 삭제 버튼이 있다(사용자 지시 2026-09-03). 서버는 지우는 대신 <b>가리므로</b>, 안 하고 치워도
 * 요청자 집계에는 「안 함」 으로 남는다 — 배정이 의미를 잃지 않는 것은 그 때문이다(TodoService.delete).
 *
 * 삭제 버튼은 마우스를 올릴 때만 나타나게 하지 않는다 — 안 할 일을 치우는 유일한 길이라,
 * 보이지 않으면 사용자는 체크로 없애려 하고, 그러면 안 한 일이 전체보기에 「끝냄」 으로 남는다.
 */
function TodoRow({
  item,
  onToggle,
  onRemove,
}: {
  item: ServerTodo;
  onToggle: () => void;
  onRemove: () => void;
}) {
  return (
    <div className="flex items-center gap-2 py-1.5 px-1 rounded-md hover:bg-slate-50">
      <input
        type="checkbox"
        checked={item.done}
        onChange={onToggle}
        aria-label={item.content}
        style={{ width: '14px', height: '14px', flexShrink: 0, accentColor: 'var(--accent)' }}
      />
      {item.kind === 'ASSIGNED' && (
        <span className="badge badge-gray flex-shrink-0" title="다른 사람이 요청한 일입니다">
          요청
        </span>
      )}
      {/* 갈 곳을 아는 배정(자료 요청 등)은 눌러서 그 건으로 간다 — "요청이 왔다" 만 뜨고 어디로
          가야 하는지 없으면 받는 사람은 메뉴를 뒤진다. */}
      {/* 한 줄로 자르고, 잘린 나머지는 `title` 로 읽게 한다. 두 줄로 접지 않는 것은 위젯 높이가
          늘어나면 메이슨리가 스팬을 다시 잡아 아래 위젯들이 통째로 밀리기 때문이다. */}
      {item.href ? (
        <Link
          to={item.href}
          className="min-w-0 flex-1 truncate hover:underline text-ui-md"
          title={item.content}
          style={{
            color: item.done ? 'var(--text-muted)' : 'var(--text-secondary)',
            textDecoration: item.done ? 'line-through' : 'none',
          }}
        >
          {item.content}
        </Link>
      ) : (
        <span
          className="min-w-0 flex-1 truncate text-ui-md"
          title={item.content}
          style={{
            color: item.done ? 'var(--text-muted)' : 'var(--text-secondary)',
            textDecoration: item.done ? 'line-through' : 'none',
          }}
        >
          {item.content}
        </span>
      )}
      {item.dueDate && !item.done && <DueBadge dueDate={item.dueDate} />}
      <button
        onClick={onRemove}
        className="btn-ghost p-0.5 flex-shrink-0"
        aria-label={`${item.content} 삭제`}
        title="이 할일 삭제"
        style={{ color: 'var(--text-muted)' }}
      >
        <MenuIcon name="x" size={12} />
      </button>
    </div>
  );
}

function DueBadge({ dueDate }: { dueDate: string }) {
  const remaining = daysUntil(dueDate);
  const overdue = remaining < 0;

  return (
    <span className={`badge flex-shrink-0 ${overdue ? 'badge-red' : remaining <= 1 ? 'badge-yellow' : 'badge-gray'}`}>
      {overdue ? `${-remaining}일 지남` : remaining === 0 ? '오늘' : `D-${remaining}`}
    </span>
  );
}

type DerivedTodo = {
  /**
   * 원본을 가리키는 합성 키. **상태를 박아 둔다** — 치운 기록은 서버에 키로 남으므로(V231),
   * 키가 영영 같으면 한 번 치운 사람에게는 다음 결재도, 다음 주 보고도 영영 뜨지 않는다.
   * 건수·주차가 달라지면 다른 키가 되어 알림이 되살아난다.
   */
  id: string;
  content: string;
  href: string;
  tone: 'normal' | 'danger';
  /** 치울 수 있는가. 화면이 계산하는 파생은 전부 치울 수 있다 — 서버 파생만 false 가 될 수 있다(V264). */
  dismissible: boolean;
};

/**
 * 사용자가 적지 않았지만 처리해야 하는 것들 중 **화면 자료로 판단하는 것**.
 * (자격증 만료처럼 인사 자료를 봐야 하는 것은 서버가 목록에 실어 보낸다 — 여기서 계산하지 않는다.)
 *
 * 볼 수 없는 화면의 할일은 만들지 않는다 — 눌러도 못 가는 항목은 안내가 아니라 방해다.
 */
function useDerivedTodos(): DerivedTodo[] {
  const { data: session } = useSession();
  const { primaryScope } = useMyProfile();

  const allowed = new Set(session?.visibleMenuKeys ?? []);
  // 관리자는 visibleMenuKeys 가 비어 오므로 전자결재는 관리자에게도 열어 준다(누구나 결재선에 들 수 있다).
  const canApprove = isAdministrator(session?.user?.userType) || allowed.has('approval');
  const items: DerivedTodo[] = [];

  const requests = useQuery({
    queryKey: queryKeys.requestBoardPosts,
    queryFn: () => mockList('requestBoardPosts'),
    enabled: allowed.has('requests'),
  });

  // 내 차례가 된 결재 대기 — 담당 위치(결재선)에 오면 여기 뜬다.
  // 결재함(`useApprovalBox`)과 같은 캐시를 쓰므로 결재 화면에 들어갔다 나와도 다시 세지 않는다.
  const approvals = useInboxCount(canApprove);

  const weeks = useWeeks();
  const sites = useSites();
  const currentWeek = findCurrentWeek(weeks.data ?? []);
  // 이번 주만 묻는다. 주차를 안 걸면 개설 이후 <b>전 현장 전 주차</b>가 홈에 딸려 온다.
  // 캐시 키에 주차를 붙이는 것은 주간업무 화면(전 주차를 받는다)과 같은 키를 쓰면 서로의 목록을
  // 덮어써, 먼저 도착한 쪽이 이긴 화면이 반쪽만 그려지기 때문이다.
  const siteTasks = useQuery({
    queryKey: [...queryKeys.siteTaskItems, currentWeek?.id ?? 'none'],
    queryFn: () => apiFetch<SiteTaskItem[]>(`/v1/site-tasks?weekId=${currentWeek?.id ?? ''}`),
    enabled: !!currentWeek && allowed.has('meetings_site') && primaryScope?.scopeType === 'site',
  });

  // 비밀번호 알림(초기 비밀번호·3개월 주기)은 서버가 만든다 — 세션의 mustChangePassword 는 로그인
  // 시점에 굳어, 방금 바꾼 사람에게도 다시 로그인할 때까지 알림이 남았다.

  const pendingApprovals = approvals.data ?? 0;
  if (pendingApprovals > 0) {
    items.push({
      id: `approvals:${pendingApprovals}`,
      content: `결재 대기 ${pendingApprovals}건`,
      href: '/approval?box=inbox',
      tone: 'danger',
      dismissible: true,
    });
  }

  const mySite = (sites.data ?? []).find((site) => site.name === primaryScope?.scopeName);

  if (currentWeek && mySite && siteTasks.data) {
    const written = siteTasks.data.some((task) => task.weekId === currentWeek.id && task.siteId === mySite.id);
    if (!written) {
      items.push({
        id: `weekly-report:${currentWeek.id}`,
        content: `${currentWeek.label} 주간보고가 아직 비어 있습니다`,
        href: '/meetings/site',
        tone: 'danger',
        dismissible: true,
      });
    }
  }

  const openRequests = (requests.data ?? []).filter((post) => !post.completed).length;
  if (openRequests > 0) {
    items.push({
      id: `requests:${openRequests}`,
      content: `미처리 업무 요청 ${openRequests}건`,
      href: '/requests',
      tone: 'normal',
      dismissible: true,
    });
  }

  return items;
}
